const CHECKOUT_ATTRIBUTION_STORAGE_KEY = "checkout_attribution";
const CHECKOUT_ATTRIBUTION_LOGGED_KEY = "checkout_attribution_logged";
const GOOGLE_ADS_CLICK_ID_TTL_MS = 90 * 24 * 60 * 60 * 1000;
const DEFAULT_ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

export const CHECKOUT_ATTRIBUTION_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "rdt_cid",
  "click_id",
  "reddit_click_id",
] as const;

export type CheckoutAttributionKey = (typeof CHECKOUT_ATTRIBUTION_KEYS)[number];

export type CheckoutAttribution = Partial<Record<CheckoutAttributionKey, string>>;
type StoredAttributionValue = {
  value: string;
  ts: number;
};
type StoredCheckoutAttribution = Partial<Record<CheckoutAttributionKey, StoredAttributionValue>>;

function getIsDebugBuild() {
  return process.env.NODE_ENV !== "production";
}

function getLocalStorage() {
  return typeof localStorage === "undefined" ? null : localStorage;
}

function getSessionStorage() {
  return typeof sessionStorage === "undefined" ? null : sessionStorage;
}

function getKeyTtlMs(key: CheckoutAttributionKey) {
  return ["gclid", "gbraid", "wbraid"].includes(key) ? GOOGLE_ADS_CLICK_ID_TTL_MS : DEFAULT_ATTRIBUTION_TTL_MS;
}

function readStoredAttributionMap(): StoredCheckoutAttribution {
  const storage = getLocalStorage();
  const raw = storage?.getItem(CHECKOUT_ATTRIBUTION_STORAGE_KEY);
  if (!raw) {
    return {};
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const now = Date.now();
    let didPruneExpired = false;
    const validEntries = CHECKOUT_ATTRIBUTION_KEYS.flatMap(key => {
      const entry = parsed?.[key];
      const parsedEntry = entry as StoredAttributionValue | undefined;
      if (!entry || typeof entry !== "object" || typeof parsedEntry?.value !== "string" || typeof parsedEntry?.ts !== "number") {
        didPruneExpired = true;
        return [];
      }

      if (now - parsedEntry.ts > getKeyTtlMs(key)) {
        didPruneExpired = true;
        return [];
      }

      return [[key, parsedEntry] as const];
    });

    const nextValue = Object.fromEntries(validEntries) as StoredCheckoutAttribution;
    if (didPruneExpired) {
      writeStoredAttributionMap(nextValue);
    }

    return nextValue;
  } catch {
    storage?.removeItem(CHECKOUT_ATTRIBUTION_STORAGE_KEY);
    return {};
  }
}

function writeStoredAttributionMap(value: StoredCheckoutAttribution) {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  if (Object.keys(value).length === 0) {
    storage.removeItem(CHECKOUT_ATTRIBUTION_STORAGE_KEY);
    return;
  }

  storage.setItem(CHECKOUT_ATTRIBUTION_STORAGE_KEY, JSON.stringify(value));
}

function toCheckoutAttribution(value: StoredCheckoutAttribution): CheckoutAttribution {
  return Object.fromEntries(
    Object.entries(value).flatMap(([key, entry]) => {
      return entry?.value ? [[key, entry.value]] : [];
    })
  ) as CheckoutAttribution;
}

function logCapturedAttributionOnce(attribution: CheckoutAttribution) {
  if (!getIsDebugBuild()) {
    return;
  }

  const storage = getSessionStorage();
  if (!storage) {
    return;
  }

  if (storage.getItem(CHECKOUT_ATTRIBUTION_LOGGED_KEY)) {
    return;
  }

  if (Object.keys(attribution).length === 0) {
    return;
  }

  console.info("[checkout-attribution] captured attribution", attribution);
  storage.setItem(CHECKOUT_ATTRIBUTION_LOGGED_KEY, "1");
}

export function captureCheckoutAttributionFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const current = readStoredAttributionMap();
  let hasChanges = false;
  const now = Date.now();

  for (const key of CHECKOUT_ATTRIBUTION_KEYS) {
    const value = params.get(key)?.trim();
    if (!value) {
      continue;
    }

    if (!current[key] || current[key]?.value !== value) {
      current[key] = { value, ts: now };
      hasChanges = true;
    }
  }

  if (hasChanges) {
    writeStoredAttributionMap(current);
  }

  logCapturedAttributionOnce(toCheckoutAttribution(current));
}

export function getStoredCheckoutAttribution() {
  if (typeof window === "undefined") {
    return {};
  }

  return toCheckoutAttribution(readStoredAttributionMap());
}

export function storeGclid(value: string) {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return;
  }

  const current = readStoredAttributionMap();
  current.gclid = { value: normalizedValue, ts: Date.now() };
  writeStoredAttributionMap(current);
}

export function getStoredGclid() {
  return getStoredCheckoutAttribution().gclid ?? null;
}

export function getStoredGoogleAdsClickAttribution() {
  const attribution = getStoredCheckoutAttribution();
  return {
    gclid: attribution.gclid ?? null,
    gbraid: attribution.gbraid ?? null,
    wbraid: attribution.wbraid ?? null,
  };
}
export function captureGclidFromLocation() {
  captureCheckoutAttributionFromLocation();
}
